import { validateEmail } from '@jusbrasil-web/shared-utils-validators'
import { FormEvent, useCallback, useState } from 'react'

import { ERROR_MESSAGES } from '../../../utils/errorMessages'

export interface ChangeEmailSubmitInput {
  newEmail: string
}

interface UseChangeEmailFormOptions {
  onValidSubmit: (input: ChangeEmailSubmitInput) => Promise<void> | void
  currentEmail?: string
}

interface FormErrors {
  newEmail?: string
  confirmEmail?: string
}

interface FormTouched {
  newEmail: boolean
  confirmEmail: boolean
}

const INITIAL_TOUCHED: FormTouched = {
  newEmail: false,
  confirmEmail: false,
}

const normalizeEmail = (value: string) => value.trim().toLowerCase()

function validateNewEmail(value: string, currentEmail?: string): string | undefined {
  if (!value) return ERROR_MESSAGES.changeEmail.email.empty

  if (!validateEmail(value)) return ERROR_MESSAGES.changeEmail.email.invalid

  if (currentEmail && normalizeEmail(value) === normalizeEmail(currentEmail))
    return ERROR_MESSAGES.changeEmail.email.sameAsCurrent

  return undefined
}

function validateConfirmEmail(value: string, newEmail: string): string | undefined {
  if (!value) return ERROR_MESSAGES.changeEmail.email.empty

  if (normalizeEmail(value) !== normalizeEmail(newEmail))
    return ERROR_MESSAGES.changeEmail.confirmEmail.mismatch

  return undefined
}

export function useChangeEmailForm({ onValidSubmit, currentEmail }: UseChangeEmailFormOptions) {
  const [newEmail, setNewEmail] = useState<string>('')
  const [confirmEmail, setConfirmEmail] = useState<string>('')
  const [touched, setTouched] = useState<FormTouched>(INITIAL_TOUCHED)
  const [errors, setErrors] = useState<FormErrors>({})

  const handleNewBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, newEmail: true }))
    setErrors((prev) => ({ ...prev, newEmail: validateNewEmail(newEmail, currentEmail) }))
  }, [newEmail, currentEmail])

  const handleConfirmBlur = useCallback(() => {
    setTouched((prev) => ({ ...prev, confirmEmail: true }))
    setErrors((prev) => ({
      ...prev,
      confirmEmail: validateConfirmEmail(confirmEmail, newEmail),
    }))
  }, [confirmEmail, newEmail])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const nextErrors: FormErrors = {
      newEmail: validateNewEmail(newEmail, currentEmail),
      confirmEmail: validateConfirmEmail(confirmEmail, newEmail),
    }

    setTouched({ newEmail: true, confirmEmail: true })
    setErrors(nextErrors)

    if (nextErrors.newEmail || nextErrors.confirmEmail) return

    await onValidSubmit({ newEmail })
  }

  return {
    newEmail: {
      value: newEmail,
      error: touched.newEmail ? errors.newEmail : undefined,
      onChange: setNewEmail,
      onBlur: handleNewBlur,
    },
    confirmEmail: {
      value: confirmEmail,
      error: touched.confirmEmail ? errors.confirmEmail : undefined,
      onChange: setConfirmEmail,
      onBlur: handleConfirmBlur,
    },
    handleSubmit,
  }
}
